import { TFunction } from 'i18next'
import CheckIcons from '@/icons/CheckIcons'
import EditIcons from '@/icons/EditIcons'
import DeleteOutlineIcons from '@/icons/DeleteOutlineIcons'
import {
    DataTableAction,
    DataTableSelectionAction,
} from '@/components/ui/data-table-customized'
import { FilterItemConfig } from '@/components/ui/data-table-customized'
import { getEntityConfig } from '@/components/Form/Shared/SimpleAsyncSelect/config'
import { BUSINESS_TYPE, BUSINESS_STATUS } from '../types/utils'
import { Business } from '../types/response'

// ============================================================================
// HRM Actions
// ============================================================================

interface HrmActionsParams {
    t: TFunction
    onLeaderConfirm: (row: Business) => void
    onBodConfirm: (row: Business) => void
    currentEmployeeId?: number
}

export const getHrmActions = ({
    t,
    onLeaderConfirm,
    onBodConfirm,
    currentEmployeeId,
}: HrmActionsParams): DataTableAction<Business>[] => [
    {
        key: 'leader-confirm',
        label: t('leader-confirm'),
        icon: <EditIcons className="w-4 h-4" />,
        variant: 'info',
        onClick: onLeaderConfirm,
        hidden: (row) =>
            row?.business_status !== 'pending' ||
            row?.employee_id === currentEmployeeId,
    },
    {
        key: 'bod-confirm',
        label: t('bom/bod-confirm'),
        icon: <CheckIcons className="w-4 h-4" />,
        variant: 'success',
        onClick: onBodConfirm,
        hidden: (row) =>
            row?.business_status !== 'pending' &&
            row?.business_status !== 'leader-approved',
    },
]

// ============================================================================
// Member Actions
// ============================================================================

interface MemberActionsParams {
    t: TFunction
    onCancel: (row: Business) => void
}

export const getMemberActions = ({
    t,
    onCancel,
}: MemberActionsParams): DataTableAction<Business>[] => [
    {
        key: 'cancel',
        label: t('cancel'),
        icon: <DeleteOutlineIcons className="w-4 h-4" />,
        variant: 'danger',
        onClick: onCancel,
        hidden: (row) => row?.business_status !== 'pending',
    },
]

// ============================================================================
// Selection Actions (HRM bulk approval)
// ============================================================================

interface SelectionActionsParams {
    t: TFunction
    onBulkApprove: (selectedRows: Business[]) => void
}

export const getSelectionActions = ({
    t,
    onBulkApprove,
}: SelectionActionsParams): DataTableSelectionAction<Business>[] => [
    {
        key: 'bulk-approve',
        label: t('approved-business'),
        icon: <CheckIcons className="w-4 h-4" />,
        variant: 'success',
        onClick: onBulkApprove,
    },
]

// ============================================================================
// Filter Items
// ============================================================================

interface FilterItemsParams {
    t: TFunction
    pageType: 'member' | 'hrm'
    filters: { department_id: number }
    employeeActiveOnly: boolean
    setValue: (name: any, value: any) => void
    setFilters: (filters: any) => void
}

export const getFilterItems = ({
    t,
    pageType,
    filters,
    employeeActiveOnly,
    setValue,
    setFilters,
}: FilterItemsParams): FilterItemConfig[] => [
    {
        name: 'business_type',
        type: 'select',
        label: t('type'),
        placeholder: t('type-placeholder'),
        toggleKey: 'showBusinessTypeFilter',
        options: BUSINESS_TYPE(t),
    },
    {
        name: 'business_status',
        type: 'select',
        label: t('status'),
        placeholder: t('status-placeholder'),
        toggleKey: 'showBusinessStatusFilter',
        options: BUSINESS_STATUS(t),
    },
    {
        name: 'business_date_range',
        type: 'daterangepicker',
        names: ['business_startdate', 'business_enddate'],
        label: t('business-startdate'),
        toggleKey: 'showDateRangeFilter',
        showPresets: true,
        clearable: true,
    },
    {
        name: 'department_id',
        type: 'department',
        enabled: pageType === 'hrm',
        toggleKey: 'showDepartmentFilter',
        onChange: (val: number) => {
            setValue('position_id', 0)
            setValue('employee_ids', [])
            setFilters({ department_id: val, position_id: 0, employee_ids: [] })
        },
    },
    {
        name: 'position_id',
        type: 'position',
        enabled: pageType === 'hrm',
        toggleKey: 'showPositionFilter',
        departmentId: filters.department_id || undefined,
    },
    {
        name: 'employee_ids',
        type: 'async-select',
        label: t('employee'),
        placeholder: t('select_employee'),
        enabled: pageType === 'hrm',
        toggleKey: 'showEmployeeFilter',
        multiple: true,
        asyncOptions: (query, page) =>
            getEntityConfig('employee').fetchOptionsWithPage(query, page || 1, {
                departmentIds: filters.department_id
                    ? [filters.department_id]
                    : undefined,
                activeOnly: employeeActiveOnly,
            }),
        getDefaultOptions: (ids) => getEntityConfig('employee').fetchByIds(ids),
        queryKey: `employee-${filters.department_id}-${employeeActiveOnly}`,
    },
]

// ============================================================================
// Filter Toggles (Settings panel)
// ============================================================================

export const getFilterToggles = (t: TFunction, pageType: 'member' | 'hrm') => [
    {
        key: 'showBusinessTypeFilter',
        label: t('show_business_type_filter', {
            defaultValue: 'Loại công tác',
        }),
        type: 'visibility' as const,
        defaultValue: true,
    },
    {
        key: 'showBusinessStatusFilter',
        label: t('show_business_status_filter', { defaultValue: 'Trạng thái' }),
        type: 'visibility' as const,
        defaultValue: true,
    },
    {
        key: 'showDateRangeFilter',
        label: t('show_date_range_filter', {
            defaultValue: 'Khoảng thời gian',
        }),
        type: 'visibility' as const,
        defaultValue: true,
    },
    ...(pageType === 'hrm'
        ? [
              {
                  key: 'showDepartmentFilter',
                  label: t('show_department_filter', {
                      defaultValue: 'Phòng ban',
                  }),
                  type: 'visibility' as const,
                  defaultValue: true,
              },
              {
                  key: 'showPositionFilter',
                  label: t('show_position_filter', { defaultValue: 'Vị trí' }),
                  type: 'visibility' as const,
                  defaultValue: true,
              },
              {
                  key: 'showEmployeeFilter',
                  label: t('show_employee_filter', {
                      defaultValue: 'Nhân viên',
                  }),
                  type: 'visibility' as const,
                  defaultValue: true,
              },
              {
                  key: 'employeeOptions',
                  label: t('employee_filter_mode', {
                      defaultValue: 'Nhân viên',
                  }),
                  type: 'options' as const,
                  singleSelect: true,
                  defaultSelectedOption: 'activeOnly',
                  options: [
                      {
                          label: t('active_employees_only'),
                          value: 'activeOnly',
                      },
                      {
                          label: t('all_employees', {
                              defaultValue: 'Toàn bộ NV',
                          }),
                          value: 'all',
                      },
                  ],
              },
          ]
        : []),
]

// ============================================================================
// Selection Preview Columns
// ============================================================================

export const getPreviewColumns = (t: TFunction) => [
    { key: 'business_id', label: 'ID' },
    { key: 'employee_name', label: t('employee_name') },
    { key: 'department_name', label: t('department_name') },
    { key: 'business_type', label: t('type') },
    { key: 'business_status', label: t('status') },
]
